'use client'

import posthog from 'posthog-js'
import { useEffect, useRef, Suspense } from 'react'
import { usePathname, useSearchParams } from 'next/navigation'
import { useSelector } from 'react-redux'
import { RootState } from 'features/store'
import { usePersistBootstrapped } from '@/hook/usePersistBootstrapped'

// Only culineerapp.com (production) reports to PostHog — dev/staging/local
// traffic must never mix into real user analytics.
const isProduction = process.env.NEXT_PUBLIC_APP_URL === 'https://culineerapp.com'

function PageTracker() {
  const pathname = usePathname()
  const searchParams = useSearchParams()

  useEffect(() => {
    if (!isProduction) return
    const url = window.location.origin + pathname + (searchParams?.toString() ? `?${searchParams.toString()}` : '')
    posthog.capture('$pageview', { $current_url: url })
  }, [pathname, searchParams])

  return null
}

export default function PostHogAnalytics() {
  const initialized = useRef(false)
  const user = useSelector((state: RootState) => state.culineerAuth.user)
  const bootstrapped = usePersistBootstrapped()

  useEffect(() => {
    const key = process.env.NEXT_PUBLIC_POSTHOG_KEY
    if (!isProduction || !key || initialized.current) return
    posthog.init(key, {
      api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST || 'https://us.i.posthog.com',
      capture_pageview: false,
      capture_pageleave: true,
      person_profiles: 'identified_only',
    })
    initialized.current = true
  }, [])

  useEffect(() => {
    // Wait for redux-persist to complete before identifying user
    if (!initialized.current || !bootstrapped) return
    if (user?.is_internal) {
      // Culineer employee/internal account — exclude from tracking entirely.
      posthog.opt_out_capturing()
    } else if (user?.user_id) {
      posthog.opt_in_capturing()
      posthog.identify(String(user.user_id), {
        email: user.email_address,
        name: user.full_name,
        user_type: user.user_type,
        membership_type: user.default_listing?.membership_type,
        enhanced: user.default_listing?.enhanced ?? false,
        subscription_status: user.subscription_status,
      })
    } else {
      posthog.reset()
    }
  }, [user?.user_id, user?.is_internal, bootstrapped])

  return (
    <Suspense fallback={null}>
      <PageTracker />
    </Suspense>
  )
}
